"use client"

import { memo, useCallback, useEffect, useMemo, useState } from "react"
import axiosInstance from "@/lib/axios"
import Link from "next/link"

const REFRESH_MS = 5 * 60 * 1000
const MARQUEE_MIN_ENTRIES = 8

const BreakingTickerEntry = ({ item, onPause, onResume, showSeparator = true }) => (
    <span className="breaking-ticker-entry">
        <Link
            href={`/news/${item.slug}`}
            className="breaking-item-link"
            onMouseEnter={onPause}
            onMouseLeave={onResume}
            onFocus={onPause}
            onBlur={onResume}
            onTouchStart={onPause}
            title={item.title}
        >
            <span className="breaking-item">{item.title}</span>
        </Link>
        {showSeparator && <span className="breaking-separator" aria-hidden="true" />}
    </span>
)

const BreakingStripe = () => {
    const [items, setItems] = useState([])
    const [loaded, setLoaded] = useState(false)
    const [paused, setPaused] = useState(false)

    const fetchBreaking = useCallback(async () => {
        try {
            const res = await axiosInstance.get('/news/breaking', { params: { limit: 15 } })
            if (res.data?.success) {
                setItems(res.data.data || [])
            }
        } catch (err) {
            console.error('Failed to load breaking news:', err)
        } finally {
            setLoaded(true)
        }
    }, [])

    useEffect(() => {
        let mounted = true

        fetchBreaking()

        const interval = setInterval(() => {
            if (mounted) fetchBreaking()
        }, REFRESH_MS)

        return () => {
            mounted = false
            clearInterval(interval)
        }
    }, [fetchBreaking])

    const marqueeEntries = useMemo(() => {
        if (items.length <= 1) return items

        const entries = []
        let index = 0
        while (entries.length < MARQUEE_MIN_ENTRIES) {
            entries.push(items[index % items.length])
            index += 1
        }
        return entries
    }, [items])

    const handleMouseEnter = () => setPaused(true)
    const handleMouseLeave = () => setPaused(false)

    if (!loaded || !items.length) return null

    const isSingle = items.length === 1
    const displayItems = isSingle ? items : marqueeEntries

    const renderTrack = (trackKey) => (
        <div
            key={trackKey}
            className={`breaking-track ${paused ? 'paused' : ''}`}
            aria-hidden={trackKey === 'duplicate' ? true : undefined}
        >
            {displayItems.map((item, idx) => (
                <BreakingTickerEntry
                    key={`${trackKey}-${item.id}-${idx}`}
                    item={item}
                    onPause={handleMouseEnter}
                    onResume={handleMouseLeave}
                />
            ))}
        </div>
    )

    return (
        <div className="site-bar site-bar--breaking" role="region" aria-label="Breaking news">
            <div className="site-container breaking-stripe__inner">
                <div className="breaking-badge" aria-hidden="true">
                    <span className="breaking-badge__dot" />
                    <span className="breaking-badge__text">Breaking</span>
                </div>

                <span className="breaking-stripe__divider" aria-hidden="true" />

                <div
                    className={`breaking-marquee-wrapper${isSingle ? ' breaking-marquee-wrapper--static' : ''}`}
                    onMouseLeave={handleMouseLeave}
                >
                    {isSingle ? (
                        <div className="breaking-static">
                            <BreakingTickerEntry
                                item={items[0]}
                                onPause={handleMouseEnter}
                                onResume={handleMouseLeave}
                                showSeparator={false}
                            />
                        </div>
                    ) : (
                        <div className="breaking-marquee">
                            {renderTrack('primary')}
                            {renderTrack('duplicate')}
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default memo(BreakingStripe)
